/**
 * Display-only formatting of canonical decimal strings (DA-06, OD-13). These functions never do
 * arithmetic and never convert to a JS number — they only regroup digits of an already-authoritative
 * server value. A formatted string is never fed back into a calculation.
 */
const CANONICAL = /^(-?)(\d+)(?:\.(\d+))?$/;

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format a decimal string with grouping. Trailing zeros of the fraction are trimmed only when
 * `trimTrailingZeros` is set; a fraction that carries value is always shown.
 */
export function formatDecimal(value: string, opts: { trimTrailingZeros?: boolean } = {}): string {
  const m = CANONICAL.exec(value);
  if (!m) throw new Error('formatDecimal expects a canonical decimal string');
  const [, sign = '', int = '0', frac = ''] = m;
  const f = opts.trimTrailingZeros ? frac.replace(/0+$/, '') : frac;
  return `${sign}${group(int)}${f ? `.${f}` : ''}`;
}

/**
 * Toman: whole Tomans only, everywhere (owner 2026-10-15, replaces OD-13's Rial fraction). The
 * fraction is cut as TEXT (toward zero — a shown balance is never more than the real one); the
 * server value stays exact.
 */
export function formatToman(value: string): string {
  const m = CANONICAL.exec(value);
  if (!m) throw new Error('formatToman expects a canonical decimal string');
  const [, sign = '', int = '0'] = m;
  return /^0+$/.test(int) ? '0' : `${sign}${group(int.replace(/^0+(?=\d)/, ''))}`;
}

/**
 * Display precision per unit (display only; the server owns the canonical scale). Toman keeps its
 * Rial fraction when present (OD-13); USDT shows cents; crypto assets show their meaningful decimals.
 */
const UNIT_DECIMALS: Record<string, number> = { USDT: 2, BTC: 8, ETH: 6, TRX: 2, SOL: 4, TON: 4 };

function fitFraction(value: string, decimals: number): string {
  const m = CANONICAL.exec(value);
  if (!m) throw new Error('formatAmount expects a canonical decimal string');
  const [, sign = '', int = '0', frac = ''] = m;
  if (/^0+$/.test(int) && /^0*$/.test(frac)) return '0'; // an authoritative zero reads «0», not «0.0000»
  // Pad or cut the fraction as TEXT (never rounds via floating point). Cutting only drops digits beyond
  // the display scale of an already-authoritative value; amounts in confirmations use formatDecimal.
  const f = (frac + '0'.repeat(decimals)).slice(0, decimals);
  return `${sign}${group(int)}${f ? `.${f}` : ''}`;
}

/** «1,250,000 تومان», «1,240.50 USDT», «0.01250000 BTC». `null` → «—» (never a fabricated zero). */
export function formatAmount(
  value: string | null | undefined,
  unit: string,
  opts: { withUnit?: boolean } = {},
): string {
  if (value === null || value === undefined) return '—';
  const n = unit === 'TOMAN' ? formatToman(value) : fitFraction(value, UNIT_DECIMALS[unit] ?? 4);
  if (opts.withUnit === false) return n;
  return `${n} ${unit === 'TOMAN' ? 'تومان' : unit}`;
}

const JALALI = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Asia/Tehran',
});
const CLOCK = new Intl.DateTimeFormat('fa-IR', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Asia/Tehran',
});
// Engines join date and time differently («… ساعت …» vs «…، …»); compose it explicitly.
const JALALI_TIME = { format: (d: Date) => `${JALALI.format(d)}، ${CLOCK.format(d)}` };

/** «۱۲ مهر ۱۴۰۵، ۲۳:۳۴» (Tehran time). `null` → «—». */
export function formatJalali(
  iso: string | null | undefined,
  opts: { time?: boolean } = { time: true },
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return (opts.time === false ? JALALI : JALALI_TIME).format(d);
}

const RELATIVE = new Intl.RelativeTimeFormat('fa', { numeric: 'auto' });

/** «۸ دقیقه پیش», «۳ روز پیش». Falls back to the Jalali date after 30 days. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const s = Math.round((d.getTime() - now.getTime()) / 1000);
  const a = Math.abs(s);
  if (a < 45) return 'همین الان';
  if (a < 3600) return RELATIVE.format(Math.round(s / 60), 'minute');
  if (a < 86_400) return RELATIVE.format(Math.round(s / 3600), 'hour');
  if (a < 30 * 86_400) return RELATIVE.format(Math.round(s / 86_400), 'day');
  return formatJalali(iso, { time: false });
}

/** Normalise user-typed digits: Persian/Arabic-Indic → Latin, Persian decimal separator → ".". */
export function normalizeDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[٫،,]/g, (c) => (c === '٫' ? '.' : ''));
}
